import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import type { TranslationLanguageInfo } from '@sahaj/types';
import en from '../locales/en.json';
import hi from '../locales/hi.json';
import zh from '../locales/zh.json';
import de from '../locales/de.json';
import es from '../locales/es.json';
import kn from '../locales/kn.json';

export type SupportedLanguage = 'en' | 'hi' | 'zh' | 'de' | 'es' | 'kn';

export interface LanguageInfo {
  code: SupportedLanguage;
  name: string;
  nativeName: string;
  flag: string;
  bcp47: string;
}

export const SUPPORTED_LANGUAGES: LanguageInfo[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧', bcp47: 'en-US' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳', bcp47: 'hi-IN' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳', bcp47: 'zh-CN' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', bcp47: 'de-DE' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', bcp47: 'es-ES' },
  { code: 'kn', name: 'Kannada', nativeName: 'ಕನ್ನಡ', flag: '🇮🇳', bcp47: 'kn-IN' },
];

export const TRANSLATION_TARGET_LANGUAGES: TranslationLanguageInfo[] = [
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'kn', name: 'Kannada', nativeName: 'ಕನ್ನಡ', flag: '🇮🇳' },
  { code: 'or', name: 'Odia', nativeName: 'ଓଡ଼ିଆ', flag: '🇮🇳' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
];

const TRANSLATION_BUNDLES: Record<SupportedLanguage, Record<string, any>> = {
  en,
  hi,
  zh,
  de,
  es,
  kn,
};

const STORAGE_KEY = 'sahaj_language';
const TRANSLATION_LANG_STORAGE_KEY = 'sahaj_translation_target_language';
const AUTO_TRANSLATE_STORAGE_KEY = 'sahaj_auto_translate';

interface LanguageContextType {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  currentLanguageInfo: LanguageInfo;
  preferredTranslationLanguage: string;
  setPreferredTranslationLanguage: (lang: string) => void;
  autoTranslate: boolean;
  setAutoTranslate: (enabled: boolean) => void;
  getLanguageName: (code: string) => string;
  t: (path: string, params?: Record<string, string | number>) => string;
  formatDate: (date: Date | string | number, options?: Intl.DateTimeFormatOptions) => string;
  formatTime: (date: Date | string | number, options?: Intl.DateTimeFormatOptions) => string;
  formatRelativeDate: (date: Date | string | number) => string;
  formatNumber: (num: number, options?: Intl.NumberFormatOptions) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

/** Helper to resolve dot-notated object path */
function resolveNestedPath(obj: any, path: string): any {
  if (!obj || typeof obj !== 'object') return undefined;
  const segments = path.split('.');
  let current: any = obj;
  for (const seg of segments) {
    if (current && typeof current === 'object' && seg in current) {
      current = current[seg];
    } else {
      return undefined;
    }
  }
  return current;
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<SupportedLanguage>(() => {
    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return 'en';
    try {
      const stored = window.localStorage?.getItem(STORAGE_KEY) as SupportedLanguage | null;
      if (stored && ['en', 'hi', 'zh', 'de', 'es', 'kn'].includes(stored)) {
        return stored;
      }
    } catch {
      // Ignore storage errors in test or sandbox environments
    }
    return 'en';
  });

  const [preferredTranslationLanguage, setPreferredTranslationLanguageState] = useState<string>(() => {
    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return 'hi';
    try {
      const stored = window.localStorage?.getItem(TRANSLATION_LANG_STORAGE_KEY);
      if (stored) return stored;
    } catch {
      // Ignore
    }
    return 'hi';
  });

  const [autoTranslate, setAutoTranslateState] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return false;
    try {
      return window.localStorage?.getItem(AUTO_TRANSLATE_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const setLanguage = useCallback((lang: SupportedLanguage) => {
    if (['en', 'hi', 'zh', 'de', 'es', 'kn'].includes(lang)) {
      setLanguageState(lang);
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem(STORAGE_KEY, lang);
        }
      } catch (err) {
        console.error('Failed to save language preference', err);
      }
    }
  }, []);

  const setPreferredTranslationLanguage = useCallback((lang: string) => {
    setPreferredTranslationLanguageState(lang);
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(TRANSLATION_LANG_STORAGE_KEY, lang);
      }
    } catch (err) {
      console.error('Failed to save translation target language preference', err);
    }
  }, []);

  const setAutoTranslate = useCallback((enabled: boolean) => {
    setAutoTranslateState(enabled);
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(AUTO_TRANSLATE_STORAGE_KEY, String(enabled));
      }
    } catch (err) {
      console.error('Failed to save auto translate preference', err);
    }
  }, []);

  const currentLanguageInfo: LanguageInfo = useMemo(() => {
    const found = SUPPORTED_LANGUAGES.find((l) => l.code === language);
    return found || {
      code: 'en',
      name: 'English',
      nativeName: 'English',
      flag: '🇬🇧',
      bcp47: 'en-US',
    };
  }, [language]);

  /** Translation resolver with parameter interpolation and guaranteed English fallback */
  const t = useCallback(
    (path: string, params?: Record<string, string | number>): string => {
      // 1. Try active language bundle
      let text = resolveNestedPath(TRANSLATION_BUNDLES[language], path);

      // 2. Fallback to English bundle if missing or non-string
      if (typeof text !== 'string' || text.trim() === '') {
        text = resolveNestedPath(TRANSLATION_BUNDLES.en, path);
      }

      // 3. Fallback to last segment if still missing
      if (typeof text !== 'string') {
        const segments = path.split('.');
        text = segments[segments.length - 1] || path;
      }

      // 4. Interpolate {paramName}
      if (params && typeof text === 'string') {
        Object.entries(params).forEach(([key, val]) => {
          text = text.replace(new RegExp(`\\{${key}\\}`, 'g'), String(val));
        });
      }

      return text;
    },
    [language]
  );

  const getLanguageName = useCallback(
    (code: string): string => {
      const match = TRANSLATION_TARGET_LANGUAGES.find((l) => l.code === code);
      if (match) return match.name;
      const key = `languages.${code}`;
      const resolved = t(key);
      return resolved !== code ? resolved : code.toUpperCase();
    },
    [t]
  );

  /** Date formatting using native Intl API */
  const formatDate = useCallback(
    (date: Date | string | number, options?: Intl.DateTimeFormatOptions): string => {
      try {
        const d = date instanceof Date ? date : new Date(date);
        if (isNaN(d.getTime())) return '';
        const bcp47 = currentLanguageInfo.bcp47;
        const formatter = new Intl.DateTimeFormat(bcp47, options ?? {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
        return formatter.format(d);
      } catch {
        return '';
      }
    },
    [currentLanguageInfo]
  );

  /** Time formatting using native Intl API */
  const formatTime = useCallback(
    (date: Date | string | number, options?: Intl.DateTimeFormatOptions): string => {
      try {
        const d = date instanceof Date ? date : new Date(date);
        if (isNaN(d.getTime())) return '';
        const bcp47 = currentLanguageInfo.bcp47;
        const formatter = new Intl.DateTimeFormat(bcp47, options ?? {
          hour: '2-digit',
          minute: '2-digit',
        });
        return formatter.format(d);
      } catch {
        return '';
      }
    },
    [currentLanguageInfo]
  );

  /** Relative date formatting with Today / Yesterday translations */
  const formatRelativeDate = useCallback(
    (date: Date | string | number): string => {
      try {
        const d = date instanceof Date ? date : new Date(date);
        if (isNaN(d.getTime())) return '';

        const now = new Date();
        const isToday =
          d.getDate() === now.getDate() &&
          d.getMonth() === now.getMonth() &&
          d.getFullYear() === now.getFullYear();

        if (isToday) {
          return t('common.today');
        }

        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        const isYesterday =
          d.getDate() === yesterday.getDate() &&
          d.getMonth() === yesterday.getMonth() &&
          d.getFullYear() === yesterday.getFullYear();

        if (isYesterday) {
          return t('common.yesterday');
        }

        return formatDate(d);
      } catch {
        return '';
      }
    },
    [t, formatDate]
  );

  /** Number formatting using native Intl API */
  const formatNumber = useCallback(
    (num: number, options?: Intl.NumberFormatOptions): string => {
      try {
        const bcp47 = currentLanguageInfo.bcp47;
        const formatter = new Intl.NumberFormat(bcp47, options);
        return formatter.format(num);
      } catch {
        return String(num);
      }
    },
    [currentLanguageInfo]
  );

  const value: LanguageContextType = useMemo(
    () => ({
      language,
      setLanguage,
      currentLanguageInfo,
      preferredTranslationLanguage,
      setPreferredTranslationLanguage,
      autoTranslate,
      setAutoTranslate,
      getLanguageName,
      t,
      formatDate,
      formatTime,
      formatRelativeDate,
      formatNumber,
    }),
    [
      language,
      setLanguage,
      currentLanguageInfo,
      preferredTranslationLanguage,
      setPreferredTranslationLanguage,
      autoTranslate,
      setAutoTranslate,
      getLanguageName,
      t,
      formatDate,
      formatTime,
      formatRelativeDate,
      formatNumber,
    ]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
